<template>
  <div class="users-page">

    <!-- Page header -->
    <div class="page-header">
      <div class="page-header-inner">
        <div>
          <div class="page-title">Utilizadores</div>
          <div class="page-subtitle">Gestão de contas e permissões</div>
        </div>
        <div class="header-stats">
          <div class="stat-pill">
            <q-icon name="people" size="14px" class="q-mr-xs" />
            {{ users.length }} total
          </div>
          <div class="stat-pill stat-pill--active">
            <q-icon name="check_circle" size="14px" class="q-mr-xs" />
            {{ users.filter(u => u.active).length }} ativos
          </div>
          <div class="stat-pill stat-pill--admin">
            <q-icon name="admin_panel_settings" size="14px" class="q-mr-xs" />
            {{ users.filter(u => u.role === 1).length }} admins
          </div>
        </div>
      </div>
    </div>

    <!-- Content -->
    <div class="content-wrapper">

      <!-- Search + loading -->
      <div class="toolbar-row">
        <q-input
          v-model="search"
          placeholder="Pesquisar por nome ou email..."
          outlined
          dense
          class="search-input"
          clearable
        >
          <template v-slot:prepend>
            <q-icon name="search" color="grey-5" />
          </template>
        </q-input>
        <q-btn
          flat round dense
          icon="refresh"
          class="refresh-btn"
          :loading="loading"
          @click="fetchUsers"
        >
          <q-tooltip>Actualizar</q-tooltip>
        </q-btn>
      </div>

      <!-- Loading skeleton -->
      <div v-if="loading" class="q-mt-md">
        <div v-for="n in 5" :key="n" class="skeleton-row">
          <q-skeleton type="circle" size="44px" />
          <div class="skeleton-lines">
            <q-skeleton type="text" width="180px" />
            <q-skeleton type="text" width="120px" />
          </div>
          <q-skeleton type="rect" width="60px" height="24px" class="q-ml-auto" />
        </div>
      </div>

      <!-- Empty -->
      <div v-else-if="filteredUsers.length === 0" class="empty-state">
        <q-icon name="people_outline" size="64px" color="grey-4" />
        <div class="empty-text">Nenhum utilizador encontrado</div>
      </div>

      <!-- User list -->
      <div v-else class="user-list">
        <div
          v-for="user in pagedUsers"
          :key="user.id"
          class="user-card"
        >
          <!-- Avatar -->
          <div class="user-avatar" :class="user.active ? 'user-avatar--active' : 'user-avatar--inactive'">
            {{ initials(user.name) }}
          </div>

          <!-- Info -->
          <div class="user-info">
            <div class="user-name">{{ user.name }}</div>
            <div class="user-meta">
              <span class="meta-item">
                <q-icon name="email" size="12px" class="q-mr-xs" />{{ user.email }}
              </span>
              <span v-if="user.phone" class="meta-item">
                <q-icon name="phone" size="12px" class="q-mr-xs" />{{ user.phone }}
              </span>
              <span class="meta-item">
                <q-icon name="calendar_today" size="12px" class="q-mr-xs" />
                {{ formatDate(user.created_at) }}
              </span>
            </div>
          </div>

          <!-- Badges -->
          <div class="user-badges">
            <span class="badge" :class="user.active ? 'badge--active' : 'badge--inactive'">
              {{ user.active ? 'Ativo' : 'Inativo' }}
            </span>
            <span class="badge" :class="user.role === 1 ? 'badge--admin' : 'badge--agent'">
              {{ user.role === 1 ? 'Admin' : 'Agente' }}
            </span>
          </div>

          <!-- Actions -->
          <div class="user-actions">
            <button
              class="action-icon"
              :class="user.active ? 'action-icon--warn' : 'action-icon--success'"
              @click="toggleActive(user)"
            >
              <q-icon :name="user.active ? 'block' : 'check_circle'" size="18px" />
              <q-tooltip>{{ user.active ? 'Desativar' : 'Ativar' }}</q-tooltip>
            </button>
            <button
              class="action-icon"
              :class="user.role === 1 ? 'action-icon--muted' : 'action-icon--primary'"
              @click="toggleAdmin(user)"
            >
              <q-icon name="admin_panel_settings" size="18px" />
              <q-tooltip>{{ user.role === 1 ? 'Remover Admin' : 'Tornar Admin' }}</q-tooltip>
            </button>
            <button
              class="action-icon action-icon--danger"
              @click="confirmDelete(user)"
            >
              <q-icon name="delete" size="18px" />
              <q-tooltip>Eliminar</q-tooltip>
            </button>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="!loading && filteredUsers.length > 0" class="list-footer">
        <span class="footer-count">
          {{ (currentPage - 1) * PAGE_SIZE + 1 }}–{{ Math.min(currentPage * PAGE_SIZE, filteredUsers.length) }}
          de {{ filteredUsers.length }} utilizadores
        </span>
        <q-pagination
          v-if="totalPages > 1"
          v-model="currentPage"
          :max="totalPages"
          :max-pages="6"
          direction-links
          boundary-links
          color="primary"
          active-design="unelevated"
          active-color="primary"
          active-text-color="white"
        />
      </div>
    </div>

    <!-- Delete dialog -->
    <q-dialog v-model="deleteDialog" persistent>
      <q-card style="min-width: 320px; border-radius: 14px;">
        <q-card-section class="row items-center q-pb-none q-pt-lg q-px-lg">
          <q-avatar icon="delete" color="negative" text-color="white" size="40px" />
          <div class="q-ml-md">
            <div class="text-subtitle1 text-weight-bold">Eliminar utilizador</div>
            <div class="text-body2 text-grey-6">Esta acção não pode ser revertida.</div>
          </div>
        </q-card-section>
        <q-card-section class="q-px-lg">
          Tem a certeza que quer eliminar <strong>{{ userToDelete?.name }}</strong>?
        </q-card-section>
        <q-card-actions align="right" class="q-pa-lg q-pt-none">
          <q-btn flat label="Cancelar" v-close-popup class="text-grey-6" />
          <q-btn unelevated label="Eliminar" color="negative" @click="deleteUser" style="border-radius: 8px;" />
        </q-card-actions>
      </q-card>
    </q-dialog>

  </div>
</template>

<script>
import { ref, computed, watch, onMounted } from 'vue';
import store from '@/vuex/store';
import Swal from 'sweetalert2';

export default {
  name: 'UsersList',

  setup() {
    const deleteDialog = ref(false);
    const userToDelete = ref(null);
    const search = ref('');
    const currentPage = ref(1);
    const PAGE_SIZE = 8;

    const columns = [
      { name: 'id',         label: 'ID',         field: 'id',         align: 'left',   sortable: true },
      { name: 'name',       label: 'Name',       field: 'name',       align: 'left',   sortable: true },
      { name: 'email',      label: 'Email',      field: 'email',      align: 'left',   sortable: true },
      { name: 'phone',      label: 'Phone',      field: 'phone',      align: 'left',   format: (val) => val || 'N/A' },
      { name: 'active',     label: 'Status',     field: 'active',     align: 'center', sortable: true },
      { name: 'role',       label: 'Role',       field: 'role',       align: 'center', sortable: true },
      { name: 'created_at', label: 'Created At', field: 'created_at', align: 'left',   sortable: true },
      { name: 'actions',    label: 'Actions',    field: 'actions',    align: 'center' }
    ];

    const users = ref([]);
    const loading = ref(false);

    const filteredUsers = computed(() => {
      if (!search.value) return users.value;
      const q = search.value.toLowerCase();
      return users.value.filter(u =>
        u.name?.toLowerCase().includes(q) ||
        u.email?.toLowerCase().includes(q)
      );
    });

    const totalPages = computed(() => Math.ceil(filteredUsers.value.length / PAGE_SIZE));

    const pagedUsers = computed(() => {
      const start = (currentPage.value - 1) * PAGE_SIZE;
      return filteredUsers.value.slice(start, start + PAGE_SIZE);
    });

    watch(search, () => { currentPage.value = 1; });

    const initials = (name) => {
      if (!name) return '?';
      return name.split(' ').slice(0, 2).map(n => n[0]).join('').toUpperCase();
    };

    const formatDate = (date) => {
      if (!date) return '—';
      return new Date(date).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
    };

    const toggleActive = (user) => {
      user.active = user.active ? 0 : 1;
      Swal.fire({
        title: 'Tem certeza?',
        text: `Você está prestes a ${user.active ? 'ativar' : 'desativar'} este usuário.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: `Sim, ${user.active ? 'Ativar' : 'Desativar'}!`
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            await store.dispatch('admin/changeUserStatus', user);
            Swal.fire({
              title: `${user.active ? 'Ativado!' : 'Desativado!'}`,
              text: 'O status do usuário foi atualizado.',
              icon: 'success'
            });
          } catch (error) {
            console.error('Error updating user status:', error);
            Swal.fire({ title: 'Error!', text: 'There was an error updating the user status.', icon: 'error' });
          }
        }
      });
    };

    const toggleAdmin = (user) => {
      user.role = user.role === 1 ? 0 : 1;
      Swal.fire({
        title: 'Tem certeza?',
        text: `Você está prestes a tornar ${user.role ? 'Admin' : 'Agente'} este usuário.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: `Sim, tornar ${user.role ? 'Admin' : 'Agente'}!`
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            await store.dispatch('admin/changeUserAdmin', user);
            Swal.fire({
              title: `${user.active ? 'Admin!' : 'Agente!'}`,
              text: 'A permissão do usuário foi atualizada.',
              icon: 'success'
            });
          } catch (error) {
            console.error('Error updating user status:', error);
            Swal.fire({ title: 'Error!', text: 'There was an error updating the user status.', icon: 'error' });
          }
        }
      });
    };

    const confirmDelete = (user) => {
      Swal.fire({ icon: 'error', title: 'Oops...', text: 'Funcionalidade ainda não disponivel!' });
      console.log('Delete user:', user);
    };

    const deleteUser = () => {
      Swal.fire({ icon: 'error', title: 'Oops...', text: 'Funcionalidade ainda não disponivel!' });
    };

    const fetchUsers = async () => {
      loading.value = true;
      try {
        const res = await store.dispatch('admin/getUsers');
        users.value = res;
      } catch (error) {
        console.error('Error fetching users:', error);
      } finally {
        loading.value = false;
      }
    };

    onMounted(() => {
      fetchUsers();
    });

    return {
      columns, users, loading, deleteDialog, userToDelete,
      search, filteredUsers, pagedUsers, totalPages, currentPage, PAGE_SIZE,
      initials, formatDate,
      toggleActive, toggleAdmin, confirmDelete, deleteUser, fetchUsers
    };
  }
};
</script>

<style scoped>
/* ── Page ─────────────────────────────────────────────── */
.users-page {
  background: #f4f6fb;
  min-height: 100vh;
}

/* ── Header ───────────────────────────────────────────── */
.page-header {
  background: #0d1b2a;
  padding: 20px 24px;
}
.page-header-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 960px;
  margin: 0 auto;
  flex-wrap: wrap;
  gap: 12px;
}
.page-title {
  font-size: 20px;
  font-weight: 700;
  color: #ffffff;
}
.page-subtitle {
  font-size: 12px;
  color: rgba(212, 175, 55, 0.75);
  letter-spacing: 0.4px;
  margin-top: 2px;
}
.header-stats {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.stat-pill {
  display: flex;
  align-items: center;
  padding: 5px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  background: rgba(255,255,255,0.08);
  color: rgba(255,255,255,0.75);
  border: 1px solid rgba(255,255,255,0.12);
}
.stat-pill--active {
  background: rgba(34, 197, 94, 0.15);
  color: #4ade80;
  border-color: rgba(34, 197, 94, 0.25);
}
.stat-pill--admin {
  background: rgba(212, 175, 55, 0.15);
  color: #d4af37;
  border-color: rgba(212, 175, 55, 0.25);
}

/* ── Content ──────────────────────────────────────────── */
.content-wrapper {
  max-width: 960px;
  margin: 28px auto;
  padding: 0 16px 48px;
}

/* ── Toolbar ──────────────────────────────────────────── */
.toolbar-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 20px;
}
.search-input {
  flex: 1;
  background: #fff;
  border-radius: 10px;
}
.refresh-btn {
  color: #64748b !important;
  background: #ffffff !important;
  border-radius: 10px !important;
  border: 1px solid #e2e8f0;
  width: 40px;
  height: 40px;
}

/* ── Skeleton ─────────────────────────────────────────── */
.skeleton-row {
  display: flex;
  align-items: center;
  gap: 16px;
  background: #fff;
  border-radius: 12px;
  padding: 16px 20px;
  margin-bottom: 8px;
}
.skeleton-lines {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* ── Empty ────────────────────────────────────────────── */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 64px 16px;
}
.empty-text {
  font-size: 15px;
  color: #94a3b8;
  margin-top: 12px;
}

/* ── User list ────────────────────────────────────────── */
.user-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.user-card {
  display: flex;
  align-items: center;
  gap: 16px;
  background: #ffffff;
  border-radius: 14px;
  padding: 16px 20px;
  border: 1px solid #e8ecf4;
  transition: box-shadow 0.18s, transform 0.18s;
}
.user-card:hover {
  box-shadow: 0 4px 20px rgba(13, 27, 42, 0.08);
  transform: translateY(-1px);
}

/* ── Avatar ───────────────────────────────────────────── */
.user-avatar {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  font-weight: 700;
  flex-shrink: 0;
  letter-spacing: 0.5px;
}
.user-avatar--active {
  background: rgba(13, 27, 42, 0.08);
  color: #0d1b2a;
}
.user-avatar--inactive {
  background: rgba(148, 163, 184, 0.15);
  color: #94a3b8;
}

/* ── Info ─────────────────────────────────────────────── */
.user-info {
  flex: 1;
  min-width: 0;
}
.user-name {
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.user-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 3px;
}
.meta-item {
  display: flex;
  align-items: center;
  font-size: 11.5px;
  color: #94a3b8;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
}

/* ── Badges ───────────────────────────────────────────── */
.user-badges {
  display: flex;
  flex-direction: column;
  gap: 5px;
  flex-shrink: 0;
}
.badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.3px;
}
.badge--active   { background: rgba(34,197,94,.12);  color: #16a34a; }
.badge--inactive { background: rgba(239,68,68,.1);   color: #dc2626; }
.badge--admin    { background: rgba(212,175,55,.15);  color: #b8962e; }
.badge--agent    { background: rgba(99,102,241,.1);   color: #4f46e5; }

/* ── Action icons ─────────────────────────────────────── */
.user-actions {
  display: flex;
  gap: 4px;
  flex-shrink: 0;
}
.action-icon {
  width: 34px;
  height: 34px;
  border-radius: 9px;
  border: 1px solid transparent;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
  color: #94a3b8;
  position: relative;
}
.action-icon--success { color: #16a34a; }
.action-icon--success:hover { background: rgba(34,197,94,.1); border-color: rgba(34,197,94,.2); }

.action-icon--warn { color: #d97706; }
.action-icon--warn:hover { background: rgba(217,119,6,.1); border-color: rgba(217,119,6,.2); }

.action-icon--primary { color: #4f46e5; }
.action-icon--primary:hover { background: rgba(99,102,241,.1); border-color: rgba(99,102,241,.2); }

.action-icon--muted { color: #94a3b8; }
.action-icon--muted:hover { background: rgba(148,163,184,.15); }

.action-icon--danger { color: #dc2626; }
.action-icon--danger:hover { background: rgba(239,68,68,.1); border-color: rgba(239,68,68,.2); }

/* ── Footer ───────────────────────────────────────────── */
.list-footer {
  text-align: center;
  font-size: 12px;
  color: #94a3b8;
  margin-top: 20px;
}
</style>
